/**
 * /financeiro/comissoes — Comissões & RT por parceiro (M2.B) · redesign FIN-6.
 *
 * Fiel ao mockup Fase 5 V2.2 (#comissoes): KPI strip + cards colapsáveis por
 * parceiro (comm-card) com tabela de lançamentos e ação "Marcar pago".
 *
 * Camada visual; consome /api/financeiro/comissoes que já existe. Nenhum
 * cálculo financeiro novo.
 *
 * Nota de fidelidade: o mockup mostra "RT médio %" e "RT X% · N pedidos" por
 * parceiro, mas o endpoint de comissões não expõe o percentual de RT. Para não
 * inventar, o 4º KPI usa o "Total no período" e o subtítulo do card usa a
 * contagem real de lançamentos.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiChevronDown, FiRefreshCw } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import {
  confirmarLancamento, formatarReais, gerarComissoesRetroativo,
  obterComissoesPorParceiro,
} from "../../../services/financeiro";
import { PageContainer } from "../../../styles/shared";

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function ddmm(iso) {
  if (!iso) return "—";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}
function iniciais(nome) {
  const partes = String(nome || "?").trim().split(/\s+/);
  return ((partes[0]?.[0] || "") + (partes[1]?.[0] || "")).toUpperCase() || "?";
}


function ParceiroCard({ bloco, podeLancar, onMarcarPago }) {
  const [aberto, setAberto] = useState(false);
  const lancs = bloco.lancamentos || [];
  return (
    <div className={"comm-card" + (aberto ? " is-open" : "")}>
      <div className="comm-head" role="button" tabIndex={0} aria-expanded={aberto}
           onClick={() => setAberto((a) => !a)}
           onKeyDown={(e) => e.key === "Enter" && setAberto((a) => !a)}>
        <FiChevronDown className="chev" size={18} aria-hidden="true" />
        <div className="comm-av">{iniciais(bloco.parceiro_nome)}</div>
        <div className="comm-id">
          <div className="comm-name ell" title={bloco.parceiro_nome}>{bloco.parceiro_nome}</div>
          <div className="comm-sub">{bloco.lancamentos_count} lançamento(s)</div>
        </div>
        <div className="comm-nums">
          <span className="comm-pay">{formatarReais(bloco.a_pagar)} a pagar</span>
          <span className="comm-paid">{formatarReais(bloco.pago)} pago no período</span>
        </div>
      </div>
      <div className="comm-drill">
        <table className="dtable">
          <colgroup>
            <col style={{ width: 120 }} /><col /><col style={{ width: 120 }} />
            <col style={{ width: 130 }} /><col style={{ width: 150 }} />
          </colgroup>
          <thead><tr>
            <th>Pedido</th><th>Lançamento</th>
            <th className="num" style={{ textAlign: "right" }}>Comissão</th>
            <th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {lancs.map((l) => {
              const pago = !!l.data_caixa;
              return (
                <tr key={l.id}>
                  <td className="mono">{l.pedido_numero || "—"}</td>
                  <td className="mono ell" title={l.numero}>{l.numero}</td>
                  <td className="num" style={{ textAlign: "right", fontWeight: 600 }}>{formatarReais(l.valor)}</td>
                  <td>{pago
                    ? <span className="badge success no-dot">Pago {ddmm(l.data_caixa)}</span>
                    : <span className="badge warning">A pagar</span>}</td>
                  <td>
                    {!pago && podeLancar && (
                      <AsyncButton variant="secondary"
                                   style={{ padding: "5px 10px", fontSize: 12 }}
                                   successMessage="Comissão marcada como paga"
                                   onClick={() => onMarcarPago(l)}>Marcar pago</AsyncButton>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default function Comissoes() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const hoje = useMemo(() => new Date(), []);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const inicio = useMemo(() => `${hoje.getFullYear()}-01-01`, [hoje]);
  const fim = useMemo(() => isoLocal(hoje), [hoje]);
  const podeLancar = isMaster || hasAllPermissions(["financeiro:lancar"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await obterComissoesPorParceiro(request, { inicio, fim });
      setDados(d);
    } catch (err) {
      setError(err?.message || "Erro ao carregar comissões.");
    } finally { setLoading(false); }
  }, [request, inicio, fim]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleMarcarPago = useCallback(async (l) => {
    await confirmarLancamento(request, l.id, { data_caixa: isoLocal(new Date()) });
    await carregar();
  }, [request, carregar]);

  const handleGerarRetroativo = useCallback(async () => {
    const r = await gerarComissoesRetroativo(request, { inicio, fim });
    await carregar();
    return r;
  }, [request, inicio, fim, carregar]);

  const t = dados?.totais || {};
  const parceiros = dados?.por_parceiro || [];

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">Comissões</span>
          </div>
          <h1 className="page-title">Comissões & RT</h1>
          <div className="page-sub">Parceiros e arquitetos · ano {hoje.getFullYear()}</div>
        </div>
        {podeLancar && (
          <AsyncButton variant="secondary"
                       successMessage="Lançamentos de comissão gerados"
                       onClick={handleGerarRetroativo}>
            <FiRefreshCw size={14} aria-hidden="true" /> Gerar lançamentos faltantes
          </AsyncButton>
        )}
      </div>

      <div className="kpi-strip-v2 standalone" style={{ marginBottom: 14 }}>
        <div className="kpi-v2"><div className="lbl">A pagar</div><div className="val" style={{ color: "var(--warning-text)" }}>{formatarReais(t.a_pagar)}</div><div className="meta">no período</div></div>
        <div className="kpi-v2"><div className="lbl">Pago no ano</div><div className="val">{formatarReais(t.pago)}</div><div className="meta">{hoje.getFullYear()} acumulado</div></div>
        <div className="kpi-v2"><div className="lbl">Parceiros ativos</div><div className="val">{parceiros.length}</div><div className="meta">com comissão no período</div></div>
        <div className="kpi-v2"><div className="lbl">Total</div><div className="val">{formatarReais(t.total)}</div><div className="meta">a pagar + pago</div></div>
      </div>

      {error && <div className="empty"><div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {error}</div></div>}

      {parceiros.length === 0 ? (
        <div className="empty"><div className="msg">
          {loading ? "Carregando…" : "Nenhuma comissão no período."}
        </div></div>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {parceiros.map((b) => (
            <ParceiroCard key={b.parceiro_id || b.parceiro_nome} bloco={b}
                          podeLancar={podeLancar} onMarcarPago={handleMarcarPago} />
          ))}
        </div>
      )}
    </PageContainer>
  );
}
